import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Creators } from "model/apiWrapper"
import { isAuthFailureSagaError, retryOnAuthFailure } from "api/with-token-retry"
import { useNotifications } from "hooks/notifications"
import { useAccountAlias, useUserId } from "resources/account"
import { getNeedSignTerms } from "modules/account-root/selectors"
import { navigateTo } from "helpers/navigation"

interface UrlDownloadResponse {
  url: string
}

function isUrlDownloadResponse(value: unknown): value is UrlDownloadResponse {
  return typeof value === "object"
    && value !== null
    && typeof (value as { url?: unknown }).url === "string"
}

function resolveHttpDownloadUrl(url: string): URL | null {
  try {
    const resolved = new URL(url, window.location.origin)
    if (resolved.protocol !== "https:" && resolved.protocol !== "http:") return null
    return resolved
  } catch {
    return null
  }
}

interface UseUrlDownloadOptions<Args extends unknown[]> {
  buildUrl: (...args: Args) => string
  errorMessage: string
  onSuccess?: () => void
}

interface UseUrlDownloadResult<Args extends unknown[]> {
  download: (...args: Args) => void
  isDownloading: boolean
}

/**
 * Imperative, authenticated download shared by the skill catalog and the agent-chat artifact panel.
 *
 * The server endpoint returns `{ url }` rather than a redirect or blob. Cross-origin signed CloudFront
 * URLs are opened via navigation — auth is header-only and a 302 would drop it. Same-origin URLs are
 * fetched with `Creators.download` and saved as a blob, because browser navigation cannot attach the
 * auth header. The authenticated-retry policy (refresh once, never while terms are pending) is owned by
 * `api/with-token-retry`, not here. `onSuccess` fires on a validated download url before the
 * download/navigate — callers must keep it non-throwing (e.g. analytics) or it aborts the download.
 */
export function useUrlDownload<Args extends unknown[]>({ buildUrl, errorMessage, onSuccess }: UseUrlDownloadOptions<Args>): UseUrlDownloadResult<Args> {
  const dispatch = useDispatch()
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  const needSignTerms = useSelector(getNeedSignTerms)
  // Mirrored into a ref so the retry policy reads terms at call time, not from the render that built the callback.
  const needSignTermsRef = useRef(needSignTerms)
  useEffect(() => {
    needSignTermsRef.current = needSignTerms
  }, [needSignTerms])
  const notifications = useNotifications()
  // useRef latch is the actual in-flight guard: it blocks a second click synchronously (two
  // clicks in one frame both pass a useState check before the re-render flips it). isDownloading
  // is UI-only (button spinner/disabled) and stays out of the callback deps so it's stable.
  const inFlightRef = useRef(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const download = useCallback((...args: Args) => {
    if (inFlightRef.current) {
      return
    }
    inFlightRef.current = true
    setIsDownloading(true)

    const downloadUrl = buildUrl(...args)

    function requestDownloadUrl(): Promise<unknown> {
      return new Promise((resolve, reject) => {
        dispatch(Creators.get(downloadUrl, resolve, reject))
      })
    }

    function completeDownload(response: unknown): Promise<unknown> | undefined {
      if (!isUrlDownloadResponse(response)) {
        throw new Error("Download response did not contain a url")
      }

      const resolvedUrl = resolveHttpDownloadUrl(response.url)
      if (resolvedUrl == null) {
        throw new Error("Download response url is not an http(s) url")
      }

      onSuccess?.()

      if (resolvedUrl.origin === window.location.origin) {
        return new Promise((resolve, reject) => {
          dispatch(Creators.download(resolvedUrl.href, resolve, reject))
        })
      }

      navigateTo(resolvedUrl.href)
      return undefined
    }

    // Fire-and-forget: the chain terminates its own errors and clears the in-flight state in finally.
    void retryOnAuthFailure({
      account: accountAlias,
      userId,
      getNeedSignTerms: () => needSignTermsRef.current,
      attempt: requestDownloadUrl,
      isAuthFailure: isAuthFailureSagaError,
    })
      .then(completeDownload)
      .catch(() => {
        notifications.error(errorMessage)
      })
      .finally(() => {
        inFlightRef.current = false
        setIsDownloading(false)
      })
  }, [buildUrl, errorMessage, onSuccess, accountAlias, userId, dispatch, notifications])

  return { download, isDownloading }
}
